/**
 * FormaRightDrawer — the Requirements Intelligence drawer that runs
 * down the right side of the Forma surface (~400px wide in the SVG).
 *
 * Wraps the existing live components (RequirementsIntelligencePanel
 * cards + QuarantinedRulesPanel + ViolationsPanel) in a `.forma-theme`
 * div so they render in Forma's light palette via CSS-variable
 * overrides. Zero code changes to the underlying components — both
 * surfaces stay in sync because they share the same Zustand stores.
 *
 * The four "tabs" (Requirements / Sources / History / Chat) at the
 * top of the drawer mirror the SVG mockup. Only "Requirements" is
 * wired for now; the others are decorative placeholders that signal
 * Vera's product roadmap inside the Forma surface.
 */
import { useState } from "preact/hooks";
import { AccProjectSourceCard } from "../AccProjectSourceCard";
import { QuarantinedRulesPanel } from "../QuarantinedRulesPanel";
import { RequirementsIntelligencePanel } from "../RequirementsIntelligencePanel";
import { SheetChecklistCard } from "../SheetChecklistCard";
import { SubmissionReadinessPanel } from "../SubmissionReadinessPanel";
import { ViolationsPanel } from "../ViolationsPanel";
import { FORMA } from "./FormaTokens";

type DrawerTab = "requirements" | "sources" | "history" | "chat";

const TABS: { id: DrawerTab; label: string }[] = [
  { id: "requirements", label: "Requirements" },
  { id: "sources", label: "Sources" },
  { id: "history", label: "History" },
  { id: "chat", label: "Chat" },
];

export function FormaRightDrawer() {
  const [tab, setTab] = useState<DrawerTab>("requirements");
  return (
    <aside
      style={{
        width: 400,
        flexShrink: 0,
        background: FORMA.panelBg,
        borderLeft: `1px solid ${FORMA.borderHard}`,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <DrawerHeader />
      <TabStrip current={tab} onChange={setTab} />
      <div
        className="forma-theme"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {tab === "requirements" && (
          <>
            <AccProjectSourceCard />
            <SubmissionReadinessPanel />
            <RequirementsIntelligencePanel />
            <SheetChecklistCard />
            <QuarantinedRulesPanel />
            <ViolationsPanel />
          </>
        )}
        {tab === "sources" && <Placeholder label="Sources tab" />}
        {tab === "history" && <Placeholder label="History tab" />}
        {tab === "chat" && <Placeholder label="Chat tab" />}
      </div>
    </aside>
  );
}

function DrawerHeader() {
  return (
    <div
      style={{
        padding: "14px 16px 8px",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span aria-hidden style={{ color: FORMA.blue, fontSize: 16 }}>
        ✦
      </span>
      <span style={{ fontSize: 15, fontWeight: 700, color: FORMA.textPrimary }}>
        Submission Readiness
      </span>
      <span
        style={{
          marginLeft: "auto",
          background: "#e5e7eb",
          color: "#374151",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          padding: "3px 8px",
          borderRadius: 999,
        }}
      >
        BETA
      </span>
    </div>
  );
}

function TabStrip({
  current,
  onChange,
}: {
  current: DrawerTab;
  onChange: (next: DrawerTab) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Requirements Intelligence sections"
      style={{
        display: "flex",
        gap: 0,
        borderBottom: `1px solid ${FORMA.borderSoft}`,
        padding: "0 12px",
      }}
    >
      {TABS.map(t => {
        const active = current === t.id;
        return (
          <button
            type="button"
            key={t.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            style={{
              background: "transparent",
              border: "none",
              padding: "10px 14px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: active ? 600 : 400,
              color: active ? FORMA.blue : FORMA.textPrimary,
              borderBottom: `3px solid ${active ? FORMA.blue : "transparent"}`,
              marginBottom: -1,
              fontFamily: "inherit",
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function Placeholder({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: 20,
        textAlign: "center",
        color: FORMA.textMuted,
        fontSize: 12,
        border: `1px dashed ${FORMA.borderSoft}`,
        borderRadius: 8,
      }}
    >
      {label} — not yet wired. The Requirements tab carries the live demo.
    </div>
  );
}
